import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Bell, BellOff, CheckCheck } from "lucide-react";

function timeAgo(iso, locale) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const diff = Date.now() - d.getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "·";
  if (mins < 60) return `${mins} min`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h} h`;
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(d);
}

export default function Notifications() {
  const { t, locale } = useI18n();
  usePageMeta(t("notif.meta_title"));
  const [rows, setRows] = useState(null);
  const [unread, setUnread] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications?limit=50");
      setRows(Array.isArray(data?.notifications) ? data.notifications : []);
      setUnread(data?.unread_count || 0);
    } catch { setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const markOne = async (n) => {
    if (n.read) return;
    try {
      await api.post(`/notifications/${n.id}/read`);
      setRows((rs) => rs.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
    } catch { /* silent */ }
  };

  const markAll = async () => {
    setBusy(true);
    try {
      await api.post("/notifications/read-all");
      setRows((rs) => rs.map((x) => ({ ...x, read: true })));
      setUnread(0);
      toast.success(t("notif.marked_all"));
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="notifications-page">
      <Seo title={t("notif.seo")} noindex />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader eyebrow={t("notif.title")} title={t("notif.title")} subtitle={t("notif.subtitle")} />
        {unread > 0 && (
          <Button variant="outline" className="gap-1.5 rounded-full" onClick={markAll} disabled={busy} data-testid="notif-mark-all">
            <CheckCheck className="h-4 w-4" /> {t("notif.mark_all")}
          </Button>
        )}
      </div>

      {rows === null ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground"><BellOff className="h-6 w-6" /></div>
          <p className="mt-4 font-display text-lg text-foreground">{t("notif.empty_title")}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t("notif.empty_sub")}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((n) => (
            <button key={n.id} onClick={() => markOne(n)} data-testid={`notif-${n.id}`}
              className={`flex w-full items-start gap-3 rounded-xl border p-3 text-left transition-colors ${n.read ? "border-border bg-card" : "border-primary/30 bg-primary/5"}`}>
              <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${n.read ? "bg-secondary text-muted-foreground" : "bg-primary/10 text-primary"}`}>
                <Bell className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="truncate font-semibold text-foreground">{n.title || t("notif.title")}</p>
                  <span className="shrink-0 text-xs text-muted-foreground">{timeAgo(n.created_at, locale)}</span>
                </div>
                {(n.body || n.message) && <p className="mt-0.5 text-sm text-muted-foreground">{n.body || n.message}</p>}
              </div>
              {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
